import React, { useEffect, useRef, useState } from "react";

import {
  ChevronDown,
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import useBulkPackages from "../../services/Order/useBulkPackages";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";

const EMPTY_FORM = {
  package_option_group_id: "",
  package_id: "",
  option_group_id: "",
  group_title: "",
  selection_type: "",
  minimum_selection: "",
  maximum_selection: "",
  quantity_rule: "",
  is_required: "0",
  display_order: "",
};

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none ";

export default function BulkPackageOptionGroups() {
  const {
    getPackages,
    getPackageOptionGroups,
    insertPackageOptionGroup,
    updatePackageOptionGroup,
    deletePackageOptionGroup,
    packageOptionGroupsLoading,
    packageOptionGroupSaving,
    packageOptionGroupDeletingId,
  } = useBulkPackages();
  const { getOptionGroups } = useBulkOptionGroups();

const [packages, setPackages] = useState([]);
const [optionGroups, setOptionGroups] = useState([]);
const [rows, setRows] = useState([]);
const [form, setForm] = useState(EMPTY_FORM);
const [modal, setModal] = useState(false);

const [mappingSearch, setMappingSearch] = useState("");

const filteredMappings = rows.filter((mapping) => {
  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const query = normalize(mappingSearch.trim());

  if (!query) return true;

  const matchedPackage = packages.find(
    (pkg) => String(pkg?.package_id) === String(mapping?.package_id),
  );

  const matchedGroup = optionGroups.find(
    (group) =>
      String(group?.option_group_id ?? group?.id) ===
      String(mapping?.option_group_id),
  );

  const isRequired = ["true", "1", "y", "yes"].includes(
    String(mapping?.is_required ?? "").toLowerCase(),
  );

  const searchableFields = [
    mapping?.package_option_group_id,
    mapping?.package_id,
    matchedPackage?.package_name,
    mapping?.option_group_id,
    matchedGroup?.group_name,
    mapping?.group_title,
    mapping?.selection_type,
    mapping?.minimum_selection,
    mapping?.maximum_selection,
    mapping?.quantity_rule,
    mapping?.display_order,
    isRequired ? "Required Yes True" : "Optional No False",
  ];

  return searchableFields.some((value) => normalize(value).includes(query));
});

const load = async () => {
  const mappingRows = await getPackageOptionGroups("");
  setRows(Array.isArray(mappingRows) ? mappingRows : []);
};
  const ensureOptions = async () => {
    const [p, g] = await Promise.all([
      packages.length ? Promise.resolve(packages) : getPackages(""),
      optionGroups.length ? Promise.resolve(optionGroups) : getOptionGroups(""),
    ]);
    setPackages(Array.isArray(p) ? p : []);
    setOptionGroups(Array.isArray(g) ? g : []);
  };

 useEffect(() => {
   load();
   ensureOptions();

   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const edit = async (row) => {
   await ensureOptions();

   setForm({
     package_option_group_id: String(row?.package_option_group_id ?? ""),
     package_id: String(row?.package_id ?? ""),
     option_group_id: String(row?.option_group_id ?? ""),
     group_title: String(row?.group_title ?? ""),
     selection_type: String(row?.selection_type ?? ""),
     minimum_selection: String(row?.minimum_selection ?? ""),
     maximum_selection: String(row?.maximum_selection ?? ""),
     quantity_rule: String(row?.quantity_rule ?? ""),

     is_required:
       row?.is_required === true ||
       row?.is_required === 1 ||
       String(row?.is_required).toLowerCase() === "true" ||
       String(row?.is_required) === "1"
         ? "1"
         : "0",

     display_order: String(row?.display_order ?? ""),
   });

   setModal(true);
 };

const submit = async (event) => {
  event.preventDefault();

  if (!String(form.option_group_id ?? "").trim()) {
    alert("Please select an Option Group.");
    return;
  }

  const payload = {
    ...form,

    is_required:
      form.is_required === true ||
      form.is_required === 1 ||
      String(form.is_required).toLowerCase() === "true" ||
      String(form.is_required) === "1"
        ? 1
        : 0,
  };

  const saved = form.package_option_group_id
    ? await updatePackageOptionGroup(payload)
    : await insertPackageOptionGroup(payload);

  if (saved) {
    setModal(false);
    setForm(EMPTY_FORM);
    await load();
  }
};

  const packageName = (id) =>
    packages.find((row) => String(row?.package_id) === String(id))
      ?.package_name || `Package #${id || "—"}`;

  const optionGroupName = (id) =>
    optionGroups.find(
      (row) => String(row?.option_group_id ?? row?.id) === String(id),
    )?.group_name || `Option Group #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Package Option Groups
          </h3>

          <p className="text-[11px] text-slate-500">
            Map option groups with packages and configure selection rules.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* SEARCH BAR */}
          <div className="relative w-full sm:w-64 lg:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              placeholder="Search mapping, package, group, ID..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />

            {mappingSearch && (
              <button
                type="button"
                onClick={() => setMappingSearch("")}
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={load}
            disabled={packageOptionGroupsLoading}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs"
          >
            <RefreshCw
              size={13}
              className={packageOptionGroupsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={async () => {
              setForm(EMPTY_FORM);
              setModal(true);
              await ensureOptions();
            }}
            className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} />
            Add Mapping
          </button>
        </div>
      </div>

      {packageOptionGroupsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border bg-white" />
      ) : !filteredMappings.length ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
          {mappingSearch.trim()
            ? "No package option groups match your search."
            : "No package option groups found."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filteredMappings.map((row, index) => {
            const id = String(row?.package_option_group_id ?? "");
            return (
              <div
                key={id || index}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <b className="text-sm text-slate-900">
                    {row?.group_title || optionGroupName(row?.option_group_id)}
                  </b>
                  <p className="text-[11px] text-slate-500">
                    ID #{id || "—"} · {packageName(row?.package_id)} ·{" "}
                    {optionGroupName(row?.option_group_id)} · Required{" "}
                    {row?.is_required || "N"} · Order{" "}
                    {row?.display_order || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => edit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  disabled={packageOptionGroupDeletingId === id}
                  onClick={async () => {
                    if (await deletePackageOptionGroup(id)) await load();
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 disabled:opacity-50"
                >
                  {packageOptionGroupDeletingId === id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
          <div className="relative w-full max-w-3xl overflow-visible rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">
                {form.package_option_group_id
                  ? "Edit Package Option Group"
                  : "Add Package Option Group"}
              </h3>
              <button type="button" onClick={() => setModal(false)}>
                <X size={17} />
              </button>
            </div>

            <form onSubmit={submit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <SearchableOptionGroupDropdown
                  label="Package"
                  optionGroups={packages}
                  idKey="package_id"
                  nameKey="package_name"
                  value={form.package_id}
                  disabled={packageOptionGroupSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      package_id: id,
                    }))
                  }
                />

                <SearchableOptionGroupDropdown
                  optionGroups={optionGroups}
                  value={form.option_group_id}
                  disabled={packageOptionGroupSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      option_group_id: id,
                    }))
                  }
                />

                <Input
                  label="Group Title"
                  name="group_title"
                  form={form}
                  setForm={setForm}
                />

                <Field label="Selection Type">
                  <select
                    name="selection_type"
                    value={form.selection_type || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        selection_type: e.target.value,
                      }))
                    }
                    className={field}
                    required
                  >
                    <option value="">Select Selection Type</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="CHOOSE_EXACTLY">Choose Exactly</option>
                    <option value="CHOOSE_MIN_MAX">Choose Min–Max</option>
                    <option value="INCLUDE_ALL">Include All</option>
                  </select>
                </Field>

                <Input
                  label="Minimum Selection"
                  name="minimum_selection"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Maximum Selection"
                  name="maximum_selection"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
                <Input
                  label="Quantity Rule"
                  name="quantity_rule"
                  form={form}
                  setForm={setForm}
                />

                <Field label="Is Required">
                  <select
                    value={form.is_required}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        is_required: e.target.value,
                      }))
                    }
                    className={field}
                  >
                    <option value="1">1</option>
                    <option value="0">0</option>
                  </select>
                </Field>

                <Input
                  label="Display Order"
                  name="display_order"
                  type="number"
                  form={form}
                  setForm={setForm}
                />
              </div>

              <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  disabled={packageOptionGroupSaving}
                  className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={packageOptionGroupSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {packageOptionGroupSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.package_option_group_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {packageOptionGroupSaving
                    ? "Saving..."
                    : form.package_option_group_id
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}



function SearchableOptionGroupDropdown({
  optionGroups = [],
  value,
  onChange,
  disabled = false,
  label = "Option Group",
  idKey = "option_group_id",
  nameKey = "group_name",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

const getId = (row) => String(row?.[idKey] ?? row?.id ?? "");

const getName = (row) => row?.[nameKey] || `${label} #${getId(row)}`;


  const selected = optionGroups.find((row) => getId(row) === String(value));

  const filtered = optionGroups.filter((row) => {
    const query = search.trim().toLowerCase();

    return (
      getName(row).toLowerCase().includes(query) || getId(row).includes(query)
    );
  });

  useEffect(() => {
    if (!open) return;

    searchRef.current?.focus();

    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  const chooseGroup = (row) => {
    onChange(getId(row));
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        Option Group
      </span>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-left text-sm shadow-sm transition outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-emerald-400 ring-2 ring-emerald-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <span
          className={`truncate ${
            selected ? "text-slate-800" : "text-slate-400"
          }`}
        >
          {selected
            ? getName(selected)
            : value
              ? `Option Group #${value}`
              : "Select Option Group"}
        </span>

        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
          {/* SEARCH BAR */}
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (filtered.length) chooseGroup(filtered[0]);
                  }

                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
                placeholder="Search option group..."
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none "
              />
            </div>
          </div>

          {/* MAX 6 OPTIONS - SCROLLBAR HIDDEN */}
          <div
            role="listbox"
            aria-label="Option Groups"
            className="max-h-[216px] overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <button
              type="button"
              role="option"
              aria-selected={!value}
              onClick={() => {
                onChange("");
                setOpen(false);
                setSearch("");
              }}
              className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                !value
                  ? "bg-emerald-50 font-medium text-emerald-700"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              Select Option Group
            </button>

            {filtered.length > 0 ? (
              filtered.map((row) => {
                const id = getId(row);
                const active = String(value) === id;

                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => chooseGroup(row)}
                    className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                      active
                        ? "bg-emerald-50 font-medium text-emerald-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate">{getName(row)}</span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-400">
                No option groups found
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input({ label, name, type = "text", form, setForm }) {
  return (
    <Field label={label}>
      <input
        type={type}
        value={form[name]}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, [name]: e.target.value }))
        }
        className={field}
      />
    </Field>
  );
}
